// ============================================================
// @munawb/hooks — useAuth
// مشترك بين Web و Mobile — لا يحتوي أي JSX
// ============================================================

import { useEffect } from 'react';
import { create } from 'zustand';
import { supabase, fetchCurrentProfile } from '@munawb/supabase';
import type { ProfileWithDept, UserRole } from '@munawb/types';

// ==============================
// Auth Store (Zustand)
// ==============================
interface AuthState {
  profile:     ProfileWithDept | null;
  isLoading:   boolean;
  isInitialized: boolean;
  setProfile:  (p: ProfileWithDept | null) => void;
  setLoading:  (v: boolean) => void;
  setInitialized: () => void;
  clearAuth:   () => void;
}

export const useAuthStore = create<AuthState>((set) => ({
  profile:       null,
  isLoading:     true,
  isInitialized: false,
  setProfile:    (profile) => set({ profile }),
  setLoading:    (isLoading) => set({ isLoading }),
  setInitialized: () => set({ isInitialized: true }),
  clearAuth:     () => set({ profile: null, isLoading: false }),
}));

// ==============================
// Hook الرئيسي
// ==============================
export function useAuth() {
  const { profile, isLoading, isInitialized, setProfile, setLoading, setInitialized, clearAuth } =
    useAuthStore();

  // تهيئة الجلسة عند mount
  useEffect(() => {
    if (isInitialized) return;

    const initAuth = async () => {
      try {
        setLoading(true);
        const { data: { session } } = await supabase.auth.getSession();
        if (session) {
          const prof = await fetchCurrentProfile();
          setProfile(prof);
        }
      } catch (err) {
        console.error('[useAuth] init error:', err);
        clearAuth();
      } finally {
        setLoading(false);
        setInitialized();
      }
    };

    initAuth();

    // الاستماع لتغييرات الجلسة
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        if (event === 'SIGNED_IN' && session) {
          const prof = await fetchCurrentProfile();
          setProfile(prof);
          setLoading(false);
        } else if (event === 'SIGNED_OUT') {
          clearAuth();
        } else if (event === 'TOKEN_REFRESHED' && session) {
          // تحديث صامت — لا نحتاج إعادة جلب الـ profile
        }
      }
    );

    return () => subscription.unsubscribe();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ==============================
  // Actions
  // ==============================
  const signIn = async (email: string, password: string) => {
    setLoading(true);
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) { setLoading(false); throw error; }
    return data;
  };

  const signOut = async () => {
    await supabase.auth.signOut();
    clearAuth();
  };

  // ==============================
  // Derived Helpers
  // ==============================
  const role: UserRole | null = profile?.role ?? null;

  const hasRole = (...roles: UserRole[]) =>
    role !== null && roles.includes(role);

  const canAccessSupervisorPage = () =>
    hasRole('Supervisor', 'HeadOfDept', 'DutyManager', 'Manager');

  const isDutyManagerPlus = () =>
    hasRole('DutyManager', 'Manager', 'Executive', 'Admin');

  const isAdmin = () => hasRole('Admin');

  return {
    profile,
    role,
    isLoading,
    isAuthenticated: !!profile && profile.status === 'active',
    hasRole,
    canAccessSupervisorPage,
    isDutyManagerPlus,
    isAdmin,
    signIn,
    signOut,
  };
}
